/** OneDrive → ローカルの反映（仕様書 §7.4）。 */

import { getDb, setMeta, getMeta } from '../db/schema';
import { graph, type GraphItem } from '../auth/GraphClient';
import { putFromRemote } from '../db/entries';
import { putDailyFromRemote } from '../db/dailies';
import { putGoalsFromRemote } from '../db/goals';
import { putTaskEntryFromRemote, putTasksFromRemote } from '../db/tasks';
import { putProjectsFromRemote } from '../db/projects';
import { bumpData } from '../db/events';
import type {
  Category,
  DailySummary,
  Entry,
  GoalsDoc,
  MonthKey,
  ProjectsDoc,
  SettingsDoc,
  TaskEntry,
  TasksDoc,
  WeeklyReview,
} from '../domain/types';
import {
  CATEGORIES_PATH,
  classifyPath,
  GOALS_PATH,
  PROJECTS_PATH,
  SETTINGS_PATH,
  TASKS_PATH,
} from './paths';
import type { MonthIndex, TombstoneDoc } from './serialize';
import { pickWinner } from './serialize';

const DELTA_LINK = 'deltaLink';
const APPROOT_PREFIX = 'approotPrefix';
const BOOTSTRAP_DONE = 'bootstrapDone';
const LAST_PULL = 'lastPull';

/** 直近のPullで何が起きたか。原因調査のために残す。 */
export interface PullStats {
  at: string;
  mode: 'bootstrap' | 'delta';
  months?: number;
  seen: number;
  applied: number;
  skippedPath: number;
  errors: string[];
}

/** delta の項目から App Folder 相対のパスを組み立てる。 */
async function relPathOf(item: GraphItem): Promise<string | null> {
  const prefix = await getMeta<string>(APPROOT_PREFIX, '');
  const parent = item.parentReference?.path;
  if (!prefix || !parent) return null;
  const full = `${parent}/${item.name}`;
  if (!full.startsWith(prefix)) return null;
  return full.slice(prefix.length).replace(/^\/+/, '');
}

async function cacheApprootPrefix(): Promise<void> {
  const root = await graph.appRoot();
  const parent = root.parentReference?.path ?? '';
  await setMeta(APPROOT_PREFIX, `${parent}/${root.name}`);
}

/** 墓標に載っている記事をローカルから消す。 */
async function applyTombstones(doc: TombstoneDoc): Promise<boolean> {
  const db = await getDb();
  let changed = false;
  for (const t of doc.items) {
    await db.put('tombstones', t);
    if (await db.get('entries', t.entryId)) {
      await db.delete('entries', t.entryId);
      const mediaKeys = await db.getAllKeysFromIndex('media', 'by_entry', t.entryId);
      await Promise.all(mediaKeys.map((k) => db.delete('media', k)));
      const strokeKeys = await db.getAllKeysFromIndex('strokes', 'by_entry', t.entryId);
      await Promise.all(strokeKeys.map((k) => db.delete('strokes', k)));
      changed = true;
    }
  }
  return changed;
}

/**
 * リモートの記事をローカルへ取り込む。
 * ローカルが未同期（_dirty）の場合は競合として扱う（§7.5）。
 */
async function applyRemoteEntry(remote: Entry, eTag: string | null): Promise<boolean> {
  const db = await getDb();
  const local = await db.get('entries', remote.id);

  if (await db.get('tombstones', remote.id)) return false; // 削除済み。復活させない

  if (!local) {
    await putFromRemote(remote, eTag);
    return true;
  }
  if (!local._dirty) {
    if (local.updatedAt === remote.updatedAt) {
      if (local._etag !== eTag) await db.put('entries', { ...local, _etag: eTag });
      return false;
    }
    await putFromRemote(remote, eTag);
    return true;
  }

  // 双方が変更されている
  const { winner } = pickWinner(local, remote);
  if (winner === remote) {
    await putFromRemote(remote, eTag);
    const cur = ((await db.get('meta', 'conflictCount'))?.value as number) ?? 0;
    await db.put('meta', { key: 'conflictCount', value: cur + 1 });
    return true;
  }
  // ローカルが勝ち。次の Push で送られる
  return false;
}

/** 一日のまとめ。記事と同じく updatedAt で勝敗を決める。 */
async function applyRemoteDaily(remote: DailySummary, eTag: string | null): Promise<boolean> {
  const db = await getDb();
  const local = await db.get('dailies', remote.date);
  if (!local) {
    await putDailyFromRemote(remote, eTag);
    return true;
  }
  if (!local._dirty) {
    if (local.updatedAt === remote.updatedAt) return false;
    await putDailyFromRemote(remote, eTag);
    return true;
  }
  const localWins =
    local.updatedAt > remote.updatedAt ||
    (local.updatedAt === remote.updatedAt && local.deviceId > remote.deviceId);
  if (localWins) return false;
  await putDailyFromRemote(remote, eTag);
  return true;
}

/** タスク入力値。競合は updatedAt → deviceId で決める。 */
async function applyRemoteTaskEntry(remote: TaskEntry, eTag: string | null): Promise<boolean> {
  const db = await getDb();
  const key = remote.kind === 'daily' ? `d:${remote.period}` : `w:${remote.period}`;
  const local = await db.get('taskEntries', key);
  if (!local) {
    await putTaskEntryFromRemote(remote, eTag);
    return true;
  }
  if (!local._dirty) {
    if (local.updatedAt === remote.updatedAt) return false;
    await putTaskEntryFromRemote(remote, eTag);
    return true;
  }
  const localWins =
    local.updatedAt > remote.updatedAt ||
    (local.updatedAt === remote.updatedAt && local.deviceId > remote.deviceId);
  if (localWins) return false;
  await putTaskEntryFromRemote(remote, eTag);
  return true;
}

async function applyCategories(remote: { categories: Category[] }): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('categories', 'readwrite');
  for (const c of remote.categories) {
    const local = await tx.store.get(c.id);
    // ローカルに無いものだけ取り込む。編集済みのものは Push 側でマージされる
    if (!local) await tx.store.put(c);
  }
  await tx.done;
}

/** 初回同期。月ごとの index.json を取り込むことで、全文検索可能な状態を一気に作る。 */
export async function bootstrap(onProgress?: (msg: string) => void): Promise<void> {
  const stats: PullStats = { at: new Date().toISOString(), mode: 'bootstrap', seen: 0, applied: 0, skippedPath: 0, errors: [] };
  await cacheApprootPrefix();

  onProgress?.('設定を取得しています…');
  const cats = await graph.getJson<{ categories: Category[] }>(CATEGORIES_PATH);
  if (cats) await applyCategories(cats.data);
  const settings = await graph.getJson<SettingsDoc>(SETTINGS_PATH);
  if (settings) await setMeta('settings', settings.data);
  const goals = await graph.getJson<GoalsDoc>(GOALS_PATH);
  if (goals) await putGoalsFromRemote(goals.data);
  const tasks = await graph.getJson<TasksDoc>(TASKS_PATH);
  if (tasks) await putTasksFromRemote(tasks.data);
  const projects = await graph.getJson<ProjectsDoc>(PROJECTS_PATH);
  if (projects) await putProjectsFromRemote(projects.data);

  onProgress?.('フォルダを確認しています…');
  const rootItems = await graph.listChildren('');
  const years = rootItems.filter((c) => c.folder && /^\d{4}$/.test(c.name));
  if (rootItems.length === 0) {
    // App Folder が本当に空なら正常。何かある筈なのに空なら列挙に失敗している
    stats.errors.push('App Folder の直下を列挙できませんでした');
  }
  const months: MonthKey[] = [];
  for (const y of years) {
    const ms = await graph.listChildren(y.name);
    for (const m of ms) {
      if (m.folder && /^\d{2}$/.test(m.name)) months.push(`${y.name}-${m.name}`);
    }
  }

  let done = 0;
  const CONCURRENCY = 4;
  const queue = [...months];
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      for (;;) {
        const month = queue.shift();
        if (!month) break;
        try {
          const idx = await graph.getJson<MonthIndex>(
            `${month.slice(0, 4)}/${month.slice(5, 7)}/index.json`,
          );
          if (idx) {
            for (const e of idx.data.entries) {
              stats.seen++;
              if (await applyRemoteEntry(e, null)) stats.applied++;
            }
            for (const d of idx.data.dailies ?? []) {
              stats.seen++;
              if (await applyRemoteDaily(d, null)) stats.applied++;
            }
            for (const t of idx.data.taskEntries ?? []) {
              stats.seen++;
              if (await applyRemoteTaskEntry(t, null)) stats.applied++;
            }
          }
        } catch (err) {
          stats.errors.push(`${month}: ${err instanceof Error ? err.message : String(err)}`);
        }
        done++;
        onProgress?.(`記録を取得しています… ${done}/${months.length}か月`);
      }
    }),
  );

  // 週次タスクは月フォルダの外にあるので個別に取り込む
  onProgress?.('週次タスクを取得しています…');
  for (const w of await graph.listChildren('tasks/weekly')) {
    if (!w.file) continue;
    try {
      const doc = await graph.getJson<TaskEntry>(`tasks/weekly/${w.name}`);
      if (doc) {
        stats.seen++;
        if (await applyRemoteTaskEntry(doc.data, doc.eTag)) stats.applied++;
      }
    } catch (err) {
      stats.errors.push(`tasks/weekly/${w.name}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  onProgress?.('削除済みの記録を確認しています…');
  for (const t of await graph.listChildren('tombstones')) {
    if (!t.file) continue;
    const doc = await graph.getJson<TombstoneDoc>(`tombstones/${t.name}`);
    if (doc) await applyTombstones(doc.data);
  }

  // 以後の差分取得の起点を作る
  let page = await graph.delta();
  while (page?.nextLink) page = await graph.delta(page.nextLink);
  if (page?.deltaLink) await setMeta(DELTA_LINK, page.deltaLink);

  stats.months = months.length;
  await setMeta(LAST_PULL, stats);
  await setMeta(BOOTSTRAP_DONE, true);
  bumpData();
}

/** 2回目以降。delta で変更点だけを取り込む。 */
export async function pullDelta(): Promise<{ changed: boolean; needsBootstrap: boolean }> {
  const link = await getMeta<string | null>(DELTA_LINK, null);
  if (!link) return { changed: false, needsBootstrap: true };
  if (!(await getMeta<string>(APPROOT_PREFIX, ''))) await cacheApprootPrefix();

  const stats: PullStats = { at: new Date().toISOString(), mode: 'delta', seen: 0, applied: 0, skippedPath: 0, errors: [] };
  let changed = false;
  let next: string | null = link;

  while (next) {
    const page = await graph.delta(next);
    if (!page) return { changed, needsBootstrap: true }; // 410 Gone

    for (const item of page.items) {
      const rel = await relPathOf(item);
      if (!rel) {
        stats.skippedPath++;
        continue;
      }
      const kind = classifyPath(rel);

      if (item.deleted) {
        const db = await getDb();
        if (kind.kind === 'entry' && (await db.get('entries', kind.id))) {
          await db.delete('entries', kind.id);
          changed = true;
        } else if (kind.kind === 'daily' && (await db.get('dailies', kind.date))) {
          await db.delete('dailies', kind.date);
          changed = true;
        } else if (kind.kind === 'taskDaily' && (await db.get('taskEntries', `d:${kind.date}`))) {
          await db.delete('taskEntries', `d:${kind.date}`);
          changed = true;
        } else if (kind.kind === 'taskWeekly' && (await db.get('taskEntries', `w:${kind.week}`))) {
          await db.delete('taskEntries', `w:${kind.week}`);
          changed = true;
        }
        continue;
      }

      switch (kind.kind) {
        case 'entry': {
          stats.seen++;
          try {
            const remote = await graph.getJson<Entry>(rel);
            if (remote && (await applyRemoteEntry(remote.data, remote.eTag))) {
              changed = true;
              stats.applied++;
            }
          } catch (err) {
            stats.errors.push(`${rel}: ${err instanceof Error ? err.message : String(err)}`);
          }
          break;
        }
        case 'tombstone': {
          const doc = await graph.getJson<TombstoneDoc>(rel);
          if (doc && (await applyTombstones(doc.data))) changed = true;
          break;
        }
        case 'categories': {
          const doc = await graph.getJson<{ categories: Category[] }>(rel);
          if (doc) {
            await applyCategories(doc.data);
            changed = true;
          }
          break;
        }
        case 'settings': {
          const doc = await graph.getJson<SettingsDoc>(rel);
          if (doc) await setMeta('settings', doc.data);
          break;
        }
        case 'goals': {
          const doc = await graph.getJson<GoalsDoc>(rel);
          if (doc) {
            await putGoalsFromRemote(doc.data);
            changed = true;
          }
          break;
        }
        case 'daily': {
          stats.seen++;
          try {
            const doc = await graph.getJson<DailySummary>(rel);
            if (doc && (await applyRemoteDaily(doc.data, doc.eTag))) {
              changed = true;
              stats.applied++;
            }
          } catch (err) {
            stats.errors.push(`${rel}: ${err instanceof Error ? err.message : String(err)}`);
          }
          break;
        }
        case 'projects': {
          const doc = await graph.getJson<ProjectsDoc>(rel);
          if (doc) {
            await putProjectsFromRemote(doc.data);
            changed = true;
          }
          break;
        }
        case 'tasks': {
          const doc = await graph.getJson<TasksDoc>(rel);
          if (doc) {
            await putTasksFromRemote(doc.data);
            changed = true;
          }
          break;
        }
        case 'taskDaily':
        case 'taskWeekly': {
          stats.seen++;
          try {
            const doc = await graph.getJson<TaskEntry>(rel);
            if (doc && (await applyRemoteTaskEntry(doc.data, doc.eTag))) {
              changed = true;
              stats.applied++;
            }
          } catch (err) {
            stats.errors.push(`${rel}: ${err instanceof Error ? err.message : String(err)}`);
          }
          break;
        }
        case 'review': {
          const doc = await graph.getJson<WeeklyReview>(rel);
          if (doc) {
            const db = await getDb();
            await db.put('reviews', doc.data);
            changed = true;
          }
          break;
        }
        // index.json は派生キャッシュなので無視する（§7.4）
        // media / drawings の実体は表示時に遅延取得する（§7.7）
        default:
          break;
      }
    }

    next = page.nextLink;
    if (page.deltaLink) {
      await setMeta(DELTA_LINK, page.deltaLink);
      next = null;
    }
  }

  await setMeta(LAST_PULL, stats);
  if (changed) bumpData();
  return { changed, needsBootstrap: false };
}

/**
 * ローカルを消さずに、OneDrive の index.json から全件を取り込み直す。
 * delta を取りこぼした疑いがあるときの復旧手段（設定画面から実行する）。
 */
export async function refetchAll(onProgress?: (msg: string) => void): Promise<PullStats> {
  await setMeta(BOOTSTRAP_DONE, false);
  await setMeta(DELTA_LINK, null);
  await bootstrap(onProgress);
  return (await getMeta<PullStats | null>(LAST_PULL, null))!;
}

export async function getLastPull(): Promise<PullStats | null> {
  return getMeta<PullStats | null>(LAST_PULL, null);
}

export async function getSyncDebug(): Promise<Record<string, unknown>> {
  return {
    bootstrapDone: await getMeta<boolean>(BOOTSTRAP_DONE, false),
    deltaLink: (await getMeta<string | null>(DELTA_LINK, null)) ? 'あり' : 'なし',
    approotPrefix: await getMeta<string>(APPROOT_PREFIX, '(未取得)'),
    lastPull: await getMeta<PullStats | null>(LAST_PULL, null),
  };
}
